
import axios from 'axios'
import store from '@/store'
import router from '@/router'

import { requestIntercepter } from './common'
import { getMainAPIClient, getDefaultAPIClient } from './openapi'


const mainAxios = axios.create();
mainAxios.interceptors.response.use((response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('error in http response ', error.response)
      store.commit('clearStore')
      if (error.response.data.message) {
        store.commit('setGlobalErrorMsg', { msg: error.response.data.message })
      }
      // We don't want to navigate to /login if we are already there
      if (router.currentRoute.path !== '/login') {
        router.push('/login');
      }
    }
    return Promise.reject(error)
  })
mainAxios.interceptors.request.use(requestIntercepter)

const defaultAxios = axios.create();
defaultAxios.interceptors.response.use((response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('error in http response ', error.response)
      store.commit('clearStore')
      if (error.response.data.message) {
        store.commit('setGlobalErrorMsg', { msg: error.response.data.message })
      }
    }
    return Promise.reject(error)
  })
defaultAxios.interceptors.request.use(requestIntercepter)

function authHeader (jwt) {
  return { Authorization: `Bearer: ${jwt}` }
}

export async function authenticate (userData) {
  return (await getMainAPIClient()).account_login(null, userData)
}

export async function authenticateFromExtension (userData) {
  return (await getDefaultAPIClient()).account_login(null, userData)
}

export async function change2FaAuthSetting (data) {
  return (await getMainAPIClient()).account_two_factor_auth_setting(null, data)
}

export async function getDemoUserToken ({ email }) {
  return (await getMainAPIClient()).account_get_demo_token({ email })
}

export async function getLoginPageNotifications () {
  return (await getMainAPIClient()).account_login_page_notifications()
}

export async function emailIsAvailable (email) {
  return (await getMainAPIClient()).account_email_is_available(null, { email })
}

export async function saveCompanyDetails (companyData) {
  return (await getMainAPIClient()).users_save_company_details(null, companyData)
}
export async function setNotificationsAsRead (ids) {
  return (await getMainAPIClient()).users_set_notifications_as_read(null, { ids })
}
export async function saveAccountName (data) {
  return (await getMainAPIClient()).user_save_account_name(null, data)
}
export async function savePaypalEmailForAffiliateCommission (paypalEmail) {
  return (await getMainAPIClient()).user_save_paypal_for_affiliate_commission(null, { paypalEmail })
}
export async function affiliatePayout(payload) {
  return (await getMainAPIClient()).admin_affiliates_execute_payout(null, payload)
}

export async function deleteCustomLogo () {
  return (await getMainAPIClient()).user_delete_custom_logo()
}
export async function uploadCustomLogo (formData) {
  return (await getMainAPIClient()).user_upload_custom_logo(null, formData)
}
export async function sendApiPartnershipContactEmail (data) {
  return (await getMainAPIClient()).user_send_api_partnership_contact_email(null, data)
}
export async function getPayAmountForPartnerApiKeyUsage () {
  return (await getMainAPIClient()).user_get_pay_amount_for_partner_api_key_usage()
}

export async function addSubAccount(data) {
  return (await getMainAPIClient()).user_add_sub_account(null, data)
}
export async function updateSubAccount(data) {
  return (await getMainAPIClient()).user_update_sub_account(null, data)
}
export async function deleteSubAccount(data) {
  return (await getMainAPIClient()).user_delete_sub_account(null, data)
}
export async function deleteSubAccounts(data) {
  return (await getMainAPIClient()).user_delete_sub_accounts(null, data)
}
export async function getSubAccountActivityLogs (params) {
  const paramsObj = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).user_get_sub_account_activity_log(paramsObj)
}
export async function addDevAccount(data) {
  return (await getMainAPIClient()).user_add_dev_account(null, data)
}
export async function deleteDevAccount(data) {
  return (await getMainAPIClient()).user_delete_dev_account(null, data)
}
export async function updateDevCallsTopupSetting(data) {
  return (await getMainAPIClient()).user_update_dev_calls_auto_top_up(null, data)
}

export async function createAPIAccessKey(data, devAccountRelationId) {
  return (await getMainAPIClient()).user_create_api_key({ devAccountRelationId }, data)
}

export async function leaveMainAccount (data) {
  return (await getMainAPIClient()).user_leave_main_account(null, data)
}

export async function saveEmailSettings (data) {
  return (await getMainAPIClient()).user_save_email_settings(null, data)
}

export async function changeSiteName(id, siteName) {
  return (await getMainAPIClient()).sites_change_name(null, { id, siteName })
}

export async function changePageName(id, pageName) {
  return (await getMainAPIClient()).pages_change_name(null, { id, pageName })
}

export async function fetchUser (isForIndependentRoutes) {
  if (isForIndependentRoutes) {
    return (await getDefaultAPIClient()).users_get()
  }
  return (await getMainAPIClient()).users_get()
}

export async function updateUser(userId, data) {
  return (await getMainAPIClient()).users_update({ user_id: userId }, data)
}

export async function fetchSites () {
  return (await getMainAPIClient()).sites_list()
}

export async function fetchSite (siteId) {
  return (await getMainAPIClient()).sites_single({ site_id: siteId })
}

export async function fetchReportsData(params = {}) {
  return (await getMainAPIClient()).reports_get_data(params)
}
export async function saveReportsData(payload) {
  return (await getMainAPIClient()).reports_save_edited_data(null, {
    ...payload.siloKeywordId ? { siloKeywordId: payload.siloKeywordId } : {},
    ...payload.reviewOptimizationOrderId ? { reviewOptimizationOrderId: payload.reviewOptimizationOrderId } : {},
    data: payload.data
  })
}

export async function updateSitePopImpactScore (siteId, data) {
  return (await getMainAPIClient()).sites_single_pop_impact_score_update({ site_id: siteId }, data)
}

export async function updateSite (siteId, data) {
  return (await getMainAPIClient()).sites_update({ site_id: siteId }, data)
}

export async function getSiteContentAudits (data) {
  return (await getMainAPIClient()).site_content_audits_list(data || {})
}

export async function createSiteContentAudit (data) {
  return (await getMainAPIClient()).site_content_audits_create(null, data)
}

export async function updateSiteContentAudit (auditId, data) {
  return (await getMainAPIClient()).site_content_audits_update({ audit_id: auditId }, data)
}

export async function createBalanceTopUpOrder (data) {
  return (await getMainAPIClient()).balance_create_top_up_order(null, data)
}

export async function updateBalanceTopUpOrder (orderId, data) {
  return (await getMainAPIClient()).balance_update_top_up_order({ order_id: orderId }, data)
}

export async function getWhiteGloveOrders (options = {}) {
  const params = {}
  if (options.includeKeywordLineItems) {
    params.includeKeywordLineItems = 1
  }
  if (options.include_keywords) {
    params.include_keywords = 1
  }
  if (options.include_ai_and_page_run_result) {
    params.include_ai_and_page_run_result = 1
  }
  return (await getMainAPIClient()).white_glove_orders_list(params)
}

export async function getWhiteGloveMissionControlSiteStats (siteId) {
  return (await getMainAPIClient()).white_glove_mission_control_site_stats({ site_id: siteId })
}

// POP Authority packages / per-site package subscriptions
export async function getPopAuthorityPackages () {
  return (await getMainAPIClient()).pop_authority_packages_list()
}

export async function getPopAuthorityCustomQuote (data) {
  return (await getMainAPIClient()).pop_authority_custom_quote(null, data)
}

export async function getPopAuthoritySiteSubscription (siteId) {
  return (await getMainAPIClient()).pop_authority_site_subscription({ site_id: siteId })
}

export async function getPopAuthoritySubscriptions () {
  return (await getMainAPIClient()).pop_authority_subscriptions_list()
}

export async function startPopAuthoritySubscriptionCheckout (siteId, data) {
  return (await getMainAPIClient()).pop_authority_subscription_checkout({ site_id: siteId }, data)
}

export async function assignPopAuthoritySubscription (siteId, data) {
  return (await getMainAPIClient()).pop_authority_subscription_assign({ site_id: siteId }, data)
}

export async function markPopAuthoritySubscriptionPaid (siteId) {
  return (await getMainAPIClient()).pop_authority_subscription_mark_paid({ site_id: siteId })
}

export async function changePopAuthoritySubscriptionPackage (siteId, data) {
  return (await getMainAPIClient()).pop_authority_subscription_change_package({ site_id: siteId }, data)
}

export async function cancelPopAuthoritySubscription (siteId, data) {
  return (await getMainAPIClient()).pop_authority_subscription_cancel({ site_id: siteId }, data || {})
}

export async function undoCancelPopAuthoritySubscription (siteId) {
  return (await getMainAPIClient()).pop_authority_subscription_undo_cancel({ site_id: siteId })
}

export async function pausePopAuthoritySubscription (siteId) {
  return (await getMainAPIClient()).pop_authority_subscription_pause({ site_id: siteId })
}

export async function resumePopAuthoritySubscription (siteId) {
  return (await getMainAPIClient()).pop_authority_subscription_resume({ site_id: siteId })
}

export async function closePopAuthoritySite (siteId, data) {
  return (await getMainAPIClient()).pop_authority_site_close({ site_id: siteId }, data || {})
}

export async function reopenPopAuthoritySite (siteId) {
  return (await getMainAPIClient()).pop_authority_site_reopen({ site_id: siteId })
}

export async function updatePopAuthorityPackageInvoice (invoiceId, data) {
  return (await getMainAPIClient()).pop_authority_package_invoice_patch({ invoice_id: invoiceId }, data)
}

export async function startPopAuthorityCycleProduction (siteId, cycleNumber) {
  return (await getMainAPIClient()).pop_authority_cycle_start_production({
    site_id: siteId,
    cycle_number: cycleNumber,
  })
}

export async function lockPopAuthorityUpcomingCycle (siteId) {
  return (await getMainAPIClient()).pop_authority_subscription_lock_upcoming_cycle({ site_id: siteId })
}

export async function getPopAuthorityDeliverables (siteId) {
  return (await getMainAPIClient()).pop_authority_deliverables_list({ site_id: siteId })
}

export async function approvePopAuthorityDeliverable (deliverableId) {
  return (await getMainAPIClient()).pop_authority_deliverable_approve({ deliverable_id: deliverableId })
}

export async function requestPopAuthorityDeliverableUpdates (deliverableId, data) {
  return (await getMainAPIClient()).pop_authority_deliverable_request_updates(
    { deliverable_id: deliverableId }, data
  )
}

export async function approveAllPopAuthorityOrderDeliverables (orderId) {
  return (await getMainAPIClient()).pop_authority_order_approve_all({ order_id: orderId })
}

export async function markPopAuthorityDeliverableRevised (deliverableId) {
  return (await getMainAPIClient()).pop_authority_deliverable_mark_revised({ deliverable_id: deliverableId })
}

export async function publishPopAuthorityDeliverable (deliverableId, data) {
  return (await getMainAPIClient()).pop_authority_deliverable_publish({ deliverable_id: deliverableId }, data)
}

export async function setPopAuthorityDeliverableLiveUrl (deliverableId, data) {
  return (await getMainAPIClient()).pop_authority_deliverable_live_url({ deliverable_id: deliverableId }, data)
}

export async function setPopAuthoritySitePublishingBlocked (siteId, data) {
  return (await getMainAPIClient()).pop_authority_site_publishing_blocked({ site_id: siteId }, data)
}

export async function retryPopAuthoritySitePublishingAccess (siteId, data) {
  return (await getMainAPIClient()).pop_authority_site_publishing_access_retry({ site_id: siteId }, data)
}

// POP Authority booster packs (Phase 8)
export async function getPopAuthorityBoosters (siteId) {
  return (await getMainAPIClient()).pop_authority_boosters_state({ site_id: siteId })
}

export async function createPopAuthorityBoosterOrder (siteId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_create({ site_id: siteId }, data)
}

export async function markPopAuthorityBoosterOrderPaid (orderId) {
  return (await getMainAPIClient()).pop_authority_booster_order_mark_paid({ order_id: orderId })
}

export async function startPopAuthorityBoosterOrderDelivery (orderId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_start_delivery({ order_id: orderId }, data || {})
}

export async function deliverPopAuthorityBoosterOrder (orderId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_deliver({ order_id: orderId }, data)
}

export async function setPopAuthorityBoosterOrderReportUrl (orderId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_report_url({ order_id: orderId }, data)
}

export async function cancelPopAuthorityBoosterOrder (orderId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_cancel({ order_id: orderId }, data || {})
}

export async function updatePopAuthorityBoosterOrder (orderId, data) {
  return (await getMainAPIClient()).pop_authority_booster_order_patch({ order_id: orderId }, data)
}

export async function recommendPopAuthorityBooster (siteId, data) {
  return (await getMainAPIClient()).pop_authority_booster_recommend({ site_id: siteId }, data)
}

export async function dismissPopAuthorityBoosterRecommendation (siteId) {
  return (await getMainAPIClient()).pop_authority_booster_recommendation_dismiss({ site_id: siteId })
}

/** The client opened the review call booking page from a project (HubSpot sync tie-break between projects). */
export async function recordPopAuthorityReviewCallBookingClick (siteId) {
  return (await getMainAPIClient()).pop_authority_review_call_booking_click({ site_id: siteId })
}

export async function sendPopAuthorityManagerMessage (siteId, data) {
  return (await getMainAPIClient()).pop_authority_contact_manager({ site_id: siteId }, data)
}

export async function fetchBulkRuns () {
  return (await getMainAPIClient()).bulk_runs_list()
}
export async function fetchBulkRun (runId) {
  return (await getMainAPIClient()).bulk_runs_single({ bulk_run_id: runId })
}

export async function deleteBulkRun (runId) {
  return (await getMainAPIClient()).bulk_runs_delete({ bulk_run_id: runId })
}

export async function downloadBulkRunZip (data) {
  const { bulkRunId } = data
  return (await getMainAPIClient()).bulk_runs_download_zip({ bulk_run_id: bulkRunId }, { type: data.type })
}

export async function fetchLsaPhrases (data) {
  return (await getMainAPIClient()).tools_fetch_lsa_phrases(null, data)
}

export async function fetchPageRuns (siteId, pageId) {
  return (await getMainAPIClient()).page_runs_list({ site_id: siteId, page_id: pageId })
}
export async function fetchPageRunsBySearchReportId (searchReportId) {
  return (await getMainAPIClient()).page_runs_by_search_report_id(null, { searchReportId })
}
export async function fetchPageRunsForExtension () {
  return (await getMainAPIClient()).page_runs_for_extension()
}
export async function fetchPageRunForExtension (pageRunId) {
  return (await getMainAPIClient()).page_runs_single_for_extension({ pageRunId })
}

export async function fetchPageRun (siteId, pageId, runId) {
  return (await getMainAPIClient()).page_runs_get({ site_id: siteId, page_id: pageId, page_run_id: runId })
}

export async function fetchPageRunBySecretKeyOrPageRunId (data) {
  return (await getDefaultAPIClient()).page_runs_by_secret_key_or_id(null, data)
}

// new endpoint
export function fetchPageRunNotes (token, siteId, pageId, runId) {
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/sites/${siteId}/pages/${pageId}/pageruns/${runId}/pagenotes/`, { headers })
}

export async function createSecretKey (siteId, pageId, runId) {
  return (await getMainAPIClient()).page_runs_create_secret_key({ site_id: siteId, page_id: pageId, page_run_id: runId }, {})
}

export async function validateSiteDomain(url) {
  return (await getMainAPIClient()).tools_validate_site_domain(null, { url })
}

export async function createSite (data) {
  return (await getMainAPIClient()).sites_create(null, data)
}

export async function fetchVariations (keyword, proxyLocation) {
  return (await getMainAPIClient()).tools_get_variations(null, {
    keyword: keyword,
    proxy_location: proxyLocation
  })
}

export async function fetchPages (siteId) {
  return (await getMainAPIClient()).pages_list({ site_id: siteId })
}

export async function createPage (data) {
  return (await getMainAPIClient()).page_runs_create({ site_id: data.siteId }, data)
}

export async function createPageRunInBackend (data) {
  return (await getMainAPIClient()).page_runs_create_pro_run({ site_id: data.siteId }, data)
}

export async function updatePageSettings (data) {
  return (await getMainAPIClient()).pages_save_settings(null, data)
}

export async function updatePagePublishStatus (data) {
  return (await getMainAPIClient()).pages_update_published_status(null, data)
}

export async function createGuardRunFromSilo (siloKeywordId) {
  return (await getMainAPIClient()).silos_create_guard_run({ silo_keyword_id: siloKeywordId })
}

export async function pauseWatchdogSetupForPages (data) {
  return (await getMainAPIClient()).pages_pause_watchdog(null, data)
}

export async function cancelWatchdogSetupForPages (data) {
  return (await getMainAPIClient()).pages_cancel_watchdog(null, data)
}

// export function updatePagePinStatus (token, data, subAccountRelationId) {
//   const headers = authHeader(token)
//   return mainAxios.post(`${API_URL}/sites/pages/update-pinned/`, data, { headers, params: { subAccountRelationId } })
// }

export async function createDemoPageRun (data) {
  return (await getMainAPIClient()).page_runs_create_demo(null, data)
}

export async function urlUsedin24Hrs (data, subAccountRelationId) {
  return (await getMainAPIClient()).page_runs_is_url_used_in_24hrs({ subAccountRelationId }, data)
}

export async function fetchData () {
  return (await getMainAPIClient()).users_user_data()
}

export async function getPageRunStatus (pageRunId) {
  return (await getMainAPIClient()).page_runs_status({ run_id: pageRunId })
}

export async function markAsReadForRunAutoStatusUpdate (pageRunId) {
  return (await getMainAPIClient()).page_runs_mark_as_read({ page_run_id: pageRunId })
}

export async function getHtmlCountData(data) {
  return (await getMainAPIClient()).tools_get_html_count_data(null, data)
}

export async function bulkExcelUploadAndValidate(csvfile) {
  let formdata = new FormData()
  formdata.append("excelFile", csvfile)
  return (await getMainAPIClient()).bulk_runs_upload_excel_and_validate(
    null,
    formdata,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}

export async function generateNewBulkRun(data) {
  // The backend expects a JSON array body; the frontend always passes the array directly.
  return (await getMainAPIClient()).bulk_runs_generate_new(null, data)
}

export async function createNewBulkRun(data) {
  return (await getMainAPIClient()).bulk_runs_new(null, data)
}


export async function cancelBulkRunTask(taskId) {
  return (await getMainAPIClient()).bulk_runs_cancel({ task_id: taskId })
}

export function downloadExcelReport(token, filePath, { secretKey = '' } = {}) {
  // The download route decides access from the session plus, in a sub-account session, the relation the
  // other requests carry; a shared report page sends the report's share key instead.
  const headers = authHeader(token)
  const params = { file: filePath }
  const relationId = store.state?.selectedSubAccountRelation?.id
  if (relationId) params.subAccountRelationId = relationId
  if (secretKey) params.secretKey = secretKey
  return mainAxios.get(`${API_URL}/download/`, { responseType: 'blob', headers, params })
}

export function downloadInvoiceFile(token, fileName) {
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/download-invoice/${encodeURIComponent(fileName)}`, { responseType: 'blob', headers })
}

export function downloadExcelReportFromUrl(token, url) {
  let headers = {}
  headers.Accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  return mainAxios.get(url, { responseType: 'blob', headers })
}

export async function fetchKeywordWizardReports(campaignId) {
  return (await getMainAPIClient()).top_level_keyword_reports_list({ campaign: campaignId })
}

export async function updateKeywordWizardReport(data) {
  return (await getMainAPIClient()).top_level_keyword_report_update(null, data)
}

export async function deleteKeywordWizardReport(reportId) {
  return (await getMainAPIClient()).top_level_keyword_report_delete({ research_id: reportId })
}

export function downloadKeywordResearchReport(token, reportId, tool) {
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/keyword-research-csv-export/${reportId}/?tool=${encodeURIComponent(tool)}`, { headers })
}

export async function updateSupportKeywordReport(data) {
  return (await getMainAPIClient()).support_keyword_report_update(null, data)
}

export async function updatePassword (data) {
  return (await getMainAPIClient()).account_update_password(null, data)
}

export async function changeEmailRequest (data) {
  return (await getMainAPIClient()).account_change_email_request(null, data)
}

export async function getAllTasksForPageRun (data) {
  return (await getMainAPIClient()).checklist_get_all_tasks(null, data)
}

export async function addOneToCheckList (data) {
  return (await getMainAPIClient()).checklist_add_one(null, data)
}

export async function addCustomTask (data) {
  return (await getMainAPIClient()).checklist_add_custom_task(null, data)
}

export async function getAllSiteTasks (data) {
  return (await getMainAPIClient()).checklist_get_all_site_tasks(null, data)
}

export async function updateCustomTask (taskId, data) {
  return (await getMainAPIClient()).checklist_update_custom_task({ task_id: taskId }, data)
}

export async function archiveOneFromCheckList (data) {
  return (await getMainAPIClient()).checklist_archive_one(null, data)
}

export async function restoreOneFromChecklist (data) {
  return (await getMainAPIClient()).checklist_restore_one(null, data)
}

export async function archiveAllChecklistWithType (data) {
  return (await getMainAPIClient()).checklist_archive_all(null, data)
}

export async function addAllToCheckList (data) {
  return (await getMainAPIClient()).checklist_add_all(null, data)
}

export async function updateChecklistState (data) {
  return (await getMainAPIClient()).checklist_update_state(null, data)
}

export async function updateChecklistDueDate (data) {
  return (await getMainAPIClient()).checklist_add_due_date(null, data)
}

export async function updateChecklistAssignee (data) {
  return (await getMainAPIClient()).checklist_assign_to(null, data)
}

export async function updateChecklistStateFromExtension (data) {
  return (await getMainAPIClient()).checklist_update_state_extension(null, data)
}

export async function createChecklistNote (data) {
  return (await getMainAPIClient()).checklist_add_note(null, data)
}

export async function deleteChecklistNote (noteId) {
  return (await getMainAPIClient()).checklist_delete_note({ note_id: noteId })
}

export async function updateChecklistNote (data) {
  return (await getMainAPIClient()).checklist_update_note(null, data)
}

export async function updateChecklistNoteFromExtension (data) {
  return (await getMainAPIClient()).checklist_note_from_extension(null, data)
}

export async function downloadExcelForChecklist (data) {
  return (await getMainAPIClient()).checklist_download_excel(null, data)
}

export async function downloadTasksForProgressNote (data) {
  return (await getMainAPIClient()).checklist_download_all_tasks(null, data)
}

export function downloadDocxForAiWriter(token, data) {
  const headers = authHeader(token)
  headers.Accept = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  return mainAxios.post(`${API_URL}/download/ai_writer/docx`, data, { responseType: 'blob', headers })
}

export async function getChecklistForExtension (data) {
  return (await getMainAPIClient()).checklist_get_for_extension(null, data)
}

export async function getContentBriefNotes (data) {
  return (await getMainAPIClient()).content_brief_notes_list(null, data)
}

export async function createContentBriefNote (data) {
  return (await getMainAPIClient()).content_brief_notes_add(null, data)
}

export async function updateContentBriefNote (data) {
  return (await getMainAPIClient()).content_brief_notes_update(null, data)
}

export async function deleteContentBriefNote (noteId) {
  return (await getMainAPIClient()).content_brief_notes_delete({ note_id: noteId })
}

export async function updateContentBriefStatus (data) {
  return (await getMainAPIClient()).content_brief_update_status(null, data)
}

export async function downloadContentBrief (data) {
  return (await getMainAPIClient()).content_brief_download(null, data)
}

export async function getProratedAmountForPurchase (userId, data) {
  return (await getMainAPIClient()).users_get_prorated_amount({ user_id: userId }, data)
}

export async function makePurchase (userId, data) {
  return (await getMainAPIClient()).users_make_purchase({ user_id: userId }, data)
}

export async function createPayPalPayment (data) {
  return (await getDefaultAPIClient()).billing_create_paypal_payment(null, data)
}

export async function createStripePayment (data) {
  return (await getDefaultAPIClient()).billing_create_stripe_payment(null, data)
}

export async function convertSubscription () {
  return (await getMainAPIClient()).billing_convert_subscription(null, {})
}

export async function buyNlpEatPackage (data) {
  return (await getMainAPIClient()).credits_buy_nlp_eat(null, data)
}

export async function buyUniversalCreditsPakcage (data) {
  return (await getMainAPIClient()).credits_buy_universal_package(null, data)
}

export async function createCreditAutoRefill (data) {
  return (await getMainAPIClient()).credit_auto_refill_create(null, data)
}

export async function listCreditAutoRefills () {
  return (await getMainAPIClient()).credit_auto_refill_list()
}

export async function updateCreditAutoRefill (refillId, data) {
  return (await getMainAPIClient()).credit_auto_refill_update({ refill_id: refillId }, data)
}

export async function cancelCreditAutoRefill (refillId) {
  return (await getMainAPIClient()).credit_auto_refill_cancel({ refill_id: refillId })
}

export function executePayPalSubscription(token, data) {
  const headers = authHeader(token)
  return mainAxios.post(`${API_URL}/execute-subscription/`, data, { headers })
}

export async function fetchYearlyUnlimitedConversionDiscount(userId) {
  return (await getMainAPIClient()).users_yearly_unlimited_conversion({ user_id: userId })
}

export async function cancelSubscription (userId, data) {
  return (await getMainAPIClient()).users_cancel_subscription({ user_id: userId }, data)
}

export async function reactivateSubscription (userId, data) {
  return (await getMainAPIClient()).users_reactivate_subscription({ user_id: userId }, data)
}

export async function updateUserSettings (data) {
  return (await getMainAPIClient()).users_update_settings(null, data)
}

export async function updateCouponCode (userId, data) {
  return (await getMainAPIClient()).users_add_coupon({ user_id: userId }, data)
}

export async function updatePaypalEmail (data) {
  return (await getMainAPIClient()).users_update_paypal_email(null, data)
}

export async function generateCancellationCode() {
  return (await getMainAPIClient()).users_generate_cancellation_code(null, null)
}

export async function addOpenAiKey (data) {
  return (await getMainAPIClient()).users_add_openai_key(null, data)
}

export async function manageAiKey (data) {
  return (await getMainAPIClient()).users_manage_ai_key(null, data)
}

export async function testAiKey (data) {
  return (await getMainAPIClient()).users_test_ai_key(null, data)
}

export async function updateAiModels (data) {
  return (await getMainAPIClient()).users_update_ai_models(null, data)
}

export function getAvailableNlpEatPackages(token) {
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/get-available-nlp-eat-packages/`, { headers })
}

export async function getAvailableUniversalPackages () {
  return (await getMainAPIClient()).credits_universal_packages()
}

export async function getAvailableAPICreditPackages () {
  return (await getMainAPIClient()).credits_api_packages()
}

export async function getAvailableWhiteGloveAddonPackages () {
  return (await getMainAPIClient()).white_glove_available_addon_packages()
}

export async function deleteAccount () {
  return (await getMainAPIClient()).users_delete_account()
}

export async function createStripeCardAdditionLink (userId) {
  return (await getMainAPIClient()).users_create_stripe_card_add_link({ user_id: userId })
}

export async function fetchPaymentSources (userId) {
  return (await getMainAPIClient()).users_get_payment_sources({ user_id: userId })
}

export function updatePaymentSource (token, userId, cardId, stripeData) {
  const headers = authHeader(token)
  return mainAxios.put(`${API_URL}/users/${userId}/payment-sources/${cardId}/`, stripeData, { headers })
}

export async function setDefaultPaymentSource (userId, cardOrPaymentId) {
  return (await getMainAPIClient()).users_set_default_payment_source({ user_id: userId }, { id: cardOrPaymentId })
}

export async function deletePaymentSource (userId, cardId) {
  return (await getMainAPIClient()).users_delete_payment_source({ user_id: userId, source_id: cardId })
}

export async function makeNewPageRun (data) {
  return (await getMainAPIClient()).page_runs_rerun({ site_id: data.siteId, page_id: data.pageId }, data)
}

export async function loadApiUsageForUser(data, devAccountRelationId) {
  return (await getMainAPIClient()).page_runs_load_api_usage_data({ devAccountRelationId }, data)
}

export async function getUsersForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_users_list(query)
}

export async function loadAdminComparisonData (primaryStartDate, primaryEndDate, secondaryStartDate, secondaryEndDate, source, aggregator) {
  return (await getMainAPIClient()).admin_stats_comparison_data({
    primaryStartDate, primaryEndDate, secondaryStartDate, secondaryEndDate, source, aggregator,
  })
}

export async function downloadAllUserExcel (data) {
  return (await getMainAPIClient()).admin_downloads_users_excel(null, data)
}

export async function downloadAllSales () {
  return (await getMainAPIClient()).admin_downloads_sales()
}

export async function updateUserAccountForAdmin (data) {
  return (await getMainAPIClient()).admin_users_update(null, data)
}

export async function resendRegistrationEmailForAdmin (data) {
  return (await getMainAPIClient()).admin_users_resend_registration(null, data)
}

export async function confirmEmailAsValidated (data) {
  return (await getMainAPIClient()).admin_users_validate_email(null, data)
}

export async function adminCreateUserNoCard (data) {
  return (await getMainAPIClient()).admin_users_create_no_card(null, data)
}

export async function adminResetUsageLimitForUser (data) {
  return (await getMainAPIClient()).admin_users_reset_usage_policy(null, data)
}

export async function adminFairUseStatusForUser (userId) {
  return (await getMainAPIClient()).admin_users_fair_use_status({ user_id: userId })
}

export async function adminPauseUsageForUser (data) {
  return (await getMainAPIClient()).admin_users_pause_usage(null, data)
}

export async function adminSetFairUseExemptForUser (data) {
  return (await getMainAPIClient()).admin_users_set_fair_use_exempt(null, data)
}

export async function adminCancelSubscription (data) {
  return (await getMainAPIClient()).admin_users_cancel_subscription(null, data)
}

export async function adminCancelCreditAutoRefill (data) {
  return (await getMainAPIClient()).credit_auto_refill_admin_cancel(null, data)
}

export async function getCouponsForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_coupons_list(query)
}

export async function autoGenCouponCode (data) {
  return (await getMainAPIClient()).admin_coupons_generate_code(null, data)
}

export async function createCouponForAdmin (data) {
  return (await getMainAPIClient()).admin_coupons_create(null, data)
}

export async function updateCouponForAdmin (data) {
  return (await getMainAPIClient()).admin_coupons_update(null, data)
}

export async function deleteCouponForAdmin (id) {
  return (await getMainAPIClient()).admin_coupons_delete({ coupon_id: id })
}

export async function addAliasToCouponForAdmin (data) {
  return (await getMainAPIClient()).admin_coupons_add_alias(null, data)
}

export async function createAdminUserComment (data) {
  return (await getMainAPIClient()).admin_users_create_comment(null, data)
}

export async function getHistoryForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_stats_history(query)
}

export async function getKeywordResearchForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_keyword_research_list(query)
}

export async function loadKeywordResearchUsageForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_keyword_research_usage(query)
}

export async function getSiteContentAuditOrdersForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_orders_site_content_audits(query)
}

export async function getSiloOrdersForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_orders_silo(query)
}

export async function getContentOrdersForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_orders_content(query)
}

// Admin > Products > POP Authority (views/openapi/admins/pop_authority_api.py)
function popAuthorityAdminQuery (params) {
  return Object.fromEntries(new URLSearchParams(params))
}

export async function getPopAuthorityCustomersForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_customers(popAuthorityAdminQuery(params))
}

// Admin > Metrics (views/openapi/admins/metrics_api.py)
export async function getAdminMetricsSeries () {
  return (await getMainAPIClient()).admin_metrics_series()
}

export async function refreshAdminMetrics () {
  return (await getMainAPIClient()).admin_metrics_refresh(null, {})
}

export async function getAdminMetricsUsers (params) {
  return (await getMainAPIClient()).admin_metrics_users(popAuthorityAdminQuery(params))
}

export async function exportAdminMetricsUsers (params) {
  return (await getMainAPIClient()).admin_metrics_users_export(popAuthorityAdminQuery(params), null, { responseType: 'blob' })
}

export async function getPopAuthoritySetupFeesForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_setup_fees(popAuthorityAdminQuery(params))
}

export async function getPopAuthorityPackageInvoicesForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_package_invoices(popAuthorityAdminQuery(params))
}

export async function getPopAuthorityContentOrdersForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_content_orders(popAuthorityAdminQuery(params))
}

export async function getPopAuthorityBoosterOrdersForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_booster_orders(popAuthorityAdminQuery(params))
}

export async function startPopAuthorityBoosterDeliveryForAdmin (orderId) {
  return (await getMainAPIClient()).admin_pop_authority_booster_start_delivery({ order_id: orderId })
}

export async function deliverPopAuthorityBoosterForAdmin (orderId, data) {
  return (await getMainAPIClient()).admin_pop_authority_booster_deliver({ order_id: orderId }, data)
}

export async function getPopAuthorityCommissionsForAdmin (params) {
  return (await getMainAPIClient()).admin_pop_authority_commissions(popAuthorityAdminQuery(params))
}

export async function updatePopAuthorityCommissionForAdmin (orderType, orderId, data) {
  return (await getMainAPIClient()).admin_pop_authority_commission_update(
    { order_type: orderType, order_id: orderId }, data)
}

// Admin > Products > SERP Validation (views/openapi/admins/serp_validation_api.py)
export async function getSerpValidationRecordsForAdmin (params) {
  return (await getMainAPIClient()).admin_serp_validation_records(popAuthorityAdminQuery(params))
}

export async function getSerpValidationRecordForAdmin (recordId) {
  return (await getMainAPIClient()).admin_serp_validation_record({ record_id: recordId })
}

export async function setSerpValidationVerdictForAdmin (recordId, data) {
  return (await getMainAPIClient()).admin_serp_validation_verdict({ record_id: recordId }, data)
}

export async function getSerpValidationTuningForAdmin (params) {
  return (await getMainAPIClient()).admin_serp_validation_tuning(popAuthorityAdminQuery(params))
}

export async function getPopAuthorityCommissionRates () {
  return (await getMainAPIClient()).admin_pop_authority_commission_rates()
}

export async function updatePopAuthorityCommissionRates (data) {
  return (await getMainAPIClient()).admin_pop_authority_commission_rates_update(null, data)
}

export async function getOrdersCommissionsForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_orders_commissions(query)
}

export async function getCampaignManagersPercentages () {
  return (await getMainAPIClient()).admin_campaign_managers_percentages_list()
}

export async function updateCampaignManagerPercentage (key, data) {
  return (await getMainAPIClient()).admin_campaign_managers_percentage_update({ key }, data)
}

export async function getWatchDogTrackHistoryForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_watchdog_track_history(query)
}

export async function getGuardRunsForUser (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).page_runs_guard_runs_list(query)
}

export async function getAIRunsForUser (params) {
  return (await getMainAPIClient()).ai_runs_list(params || {})
}

export async function getLatestAIRunForSite (params) {
  return (await getMainAPIClient()).ai_runs_get_latest_for_site(params || {})
}

export async function getAIRunsForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_ai_runs_list(query)
}

export async function getCreditUsageForUser (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).credits_logs_list(query)
}

export async function getKeywordResearchCreditUsageForUser (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).credits_keyword_research_logs_list(query)
}

export async function getBulkCreditLog (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).credits_bulk_logs_list(query)
}

export async function getAllUsersForAdmin () {
  return (await getMainAPIClient()).admin_users_all()
}

export async function getAllNotificationsForAdmin (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).admin_notifications_list(query)
}

export async function createAdminNotification (data) {
  return (await getMainAPIClient()).admin_notifications_create(null, data)
}

export async function updateNotificationForAdmin (notification) {
  return (await getMainAPIClient()).admin_notifications_update(null, notification)
}

export async function deleteNotificationForAdmin (id) {
  return (await getMainAPIClient()).admin_notifications_delete({ id })
}

export async function getSales (startDate, endDate) {
  return (await getMainAPIClient()).admin_stats_sales({ startDate, endDate })
}

export async function getNewUserStats (startDate, endDate) {
  return (await getMainAPIClient()).admin_stats_new_users({ startDate, endDate })
}

export async function getRunsPerDay (startDate, endDate) {
  return (await getMainAPIClient()).admin_stats_runs({ startDate, endDate })
}

export function getTotalUsagePerDay (token, days) {
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/admin/user-usage/?days=${days}`, { headers })
}

export async function getTotalUsers () {
  return (await getMainAPIClient()).admin_users_totals()
}

export async function getAffiliateSales () {
  return (await getMainAPIClient()).admin_affiliates_sales_list()
}

export async function getAffiliatePayouts () {
  return (await getMainAPIClient()).admin_affiliates_payouts_list()
}

export async function removeFromPayout (data) {
  return (await getMainAPIClient()).admin_affiliates_remove_from_payout(null, data)
}

export async function executePayout (data) {
  return (await getMainAPIClient()).admin_affiliates_execute_payouts(null, data)
}

export async function getPaypalRefunds () {
  return (await getMainAPIClient()).admin_paypal_refunds_list()
}

export async function executePaypalRefund (data) {
  return (await getMainAPIClient()).admin_paypal_refunds_execute(null, data)
}

export async function testCancelledWatchdogSetup (data) {
  return (await getMainAPIClient()).admin_watchdog_test_cancelled_campaign(null, data)
}


export async function deleteSite (siteId) {
  return (await getMainAPIClient()).sites_single_delete({ site_id: siteId })
}

export async function deleteSites (siteIds) {
  return (await getMainAPIClient()).sites_delete_multiple(null, { siteIds })
}

export async function deletePage (siteId, pageId) {
  return (await getMainAPIClient()).pages_delete({ site_id: siteId, page_id: pageId })
}

export async function deletePages (siteId, pageIds) {
  return (await getMainAPIClient()).pages_delete_multiple({ site_id: siteId }, { pageIds })
}

export async function updatePinPageRun (data) {
  return (await getMainAPIClient()).page_runs_update_pin(null, data)
}

export async function deletePageRun (siteId, pageId, pageRunId) {
  return (await getMainAPIClient()).page_runs_delete({ site_id: siteId, page_id: pageId, page_run_id: pageRunId })
}

export async function deletePageRuns (siteId, pageId, pageRunIds) {
  return (await getMainAPIClient()).page_runs_delete_multiple({ site_id: siteId, page_id: pageId }, { pageRunIds })
}

export async function removeTermFromCB (data) {
  return (await getMainAPIClient()).page_runs_cb_remove_term(
    { site_id: data.siteId, page_id: data.pageId, page_run_id: data.pageRunId }, data)
}

export async function recoverTermForCB (data) {
  return (await getMainAPIClient()).page_runs_cb_recover_term(
    { site_id: data.siteId, page_id: data.pageId, page_run_id: data.pageRunId }, data)
}

export async function removeTermsFromCB (data) {
  return (await getMainAPIClient()).page_runs_cb_remove_terms(
    { site_id: data.siteId, page_id: data.pageId, page_run_id: data.pageRunId }, data)
}

export async function toggleScoringMethodOfCB (data) {
  return (await getMainAPIClient()).page_runs_toggle_cb_scoring({ page_run_id: data.pageRunId }, data)
}

export async function setContentBriefCustomWordCount (data) {
  const { pageRunId, ...body } = data
  return (await getMainAPIClient()).page_runs_set_cb_word_count({ page_run_id: pageRunId }, body)
}

export async function acknowledgeForContentBriefScoreNotChanged (data) {
  return (await getDefaultAPIClient()).content_brief_acknowledge_score_modal(null, data)
}

export async function updatePageRunNote (data) {
  return (await getMainAPIClient()).page_runs_save_note(
    { site_id: data.siteId, page_id: data.pageId, page_run_id: data.pageRunId }, data)
}

export async function fetchValidatedCoupons (promoCode, isRegisterPage) {
  return (await getDefaultAPIClient()).billing_validate_coupons({ promoCode, isRegisterPage: String(isRegisterPage) })
}

export async function fetchProxies () {
  return (await getDefaultAPIClient()).proxies_list()
}

export async function getUserNotifications (userId) {
  return (await getMainAPIClient()).users_get_notifications({ user_id: userId })
}

export async function deleteUserNotification (userId, notificationId) {
  return (await getMainAPIClient()).users_delete_notification({ user_id: userId, notification_id: notificationId })
}

export async function fetchInvoiceData (userId) {
  return (await getMainAPIClient()).users_get_invoices({ user_id: userId })
}

export async function exportInvoices (userId, data) {
  return (await getMainAPIClient()).users_export_invoices({ user_id: userId }, data)
}

export async function exportRefundInvoices (userId, data) {
  return (await getMainAPIClient()).users_export_refund_invoices({ user_id: userId }, data)
}

export async function updateScoringStrategy (data) {
  return (await getMainAPIClient()).page_runs_strategy_score({ pagerun_id: data.pageRunId }, data)
}

export async function updateSummaryReportDay(data) {
  return (await getMainAPIClient()).users_update_summary_day(null, data)
}

export async function neverMindClicked() {
  return (await getMainAPIClient()).users_never_mind_clicked(null, {})
}

export async function testRunForSummaryReport() {
  return (await getMainAPIClient()).users_test_summary_email()
}

export async function updateRecommendationsForPage (data) {
  return (await getMainAPIClient()).pages_update_recommendations({ site_id: data.siteId, page_id: data.pageId }, data)
}

export async function validatePageAnalysisUrls(data) {
  return (await getMainAPIClient()).tools_validate_urls(null, data)
}

export async function validateUrlForEditor(data) {
  return (await getMainAPIClient()).tools_validate_url_for_editor(null, data)
}

export function fetchTaskResult(taskId) {
  return mainAxios.get(`${API_URL}/task/${taskId}/results/`)
}

export async function submitGoogleSearchTask(data) {
  return (await getMainAPIClient()).tools_parse_google_search(null, data)
}

export async function filterGoogleVariations(keyword, secondaryKeywords, variations) {
  return (await getMainAPIClient()).tools_filter_google_variations(null, { keyword, secondaryKeywords, variations })
}

export async function fetchGoogleSearchLocations() {
  return (await getDefaultAPIClient()).resources_google_search_locations()
}

export async function saveProjectSettings(data) {
  return (await getMainAPIClient()).sites_save_settings(null, data)
}

// export function updateProjectPinStatus(token, data) { // data { siteId, isPinned }
//   const headers = authHeader(token)
//   return mainAxios.post(`${API_URL}/sites/update-pinned/`, data, { headers })
// }

export async function saveRevisionHistoryForContentEditor (data) {
  return (await getMainAPIClient()).content_editor_save_revision(null, data)
}

export async function uploadContentEditorImage(imageFile, data, subAccountRelationId) {
  const formdata = new FormData()
  formdata.append('img', imageFile)
  formdata.append('pageRunId', data.pageRunId || '')
  formdata.append('secretKey', data.secretKey || '')
  return (await getMainAPIClient()).content_editor_upload_image(
    { subAccountRelationId, relId: subAccountRelationId },
    formdata,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
}

export async function saveEditedSourceCode (data) {
  return (await getMainAPIClient()).content_editor_save_source(null, data)
}
export function saveEditedHtml(token, data, subAccountRelationId) {
  const headers = authHeader(token);
  return mainAxios.post(`${API_URL}/reports/save-edited-html/`, data, { headers, params: { subAccountRelationId } });
}

export async function loadSavedSourceCode (data) {
  return (await getMainAPIClient()).content_editor_load_source(null, data)
}
export async function copyToContentEditor (data) {
  return (await getMainAPIClient()).content_editor_copy(null, data)
}
export function downloadContentEditorZip(token, data, subAccountRelationId) {
  const headers = authHeader(token)
  return mainAxios.post(`${API_URL}/download-content-editor-zip/`, data, {
    headers,
    responseType: "blob",
    params: { subAccountRelationId }
  })
}
export async function loadForSpecificRevisionData (data) {
  return (await getMainAPIClient()).content_editor_load_revision(null, data)
}

export async function getWeeklyPageRunStats () {
  return (await getMainAPIClient()).page_runs_weekly_stats()
}

export async function unsubscribeWatchdogMadeByAdmin (data) {
  return (await getDefaultAPIClient()).pages_unsubscribe_watchdog_admin(null, data)
}


// test api endpoint
export function openApiTest ( data ) {
  return defaultAxios.post(`${API_URL}/openai-test/`, data)
}

export async function getGenerateSchema (data) {
  return (await getMainAPIClient()).page_runs_generate_ai_schema(null, data)
}

export async function getRequestSchema (data) {
  return (await getMainAPIClient()).page_runs_request_ai_schema(null, data)
}

export async function getPromptForAI (data) {
  return (await getMainAPIClient()).ai_runs_get_prompt(null, data)
}

export async function getAIAnswerForContentPrompt (data) {
  return (await getMainAPIClient()).ai_runs_get_content_prompt_result(null, data)
}

export async function getAnswerWithAI (data) {
  return (await getMainAPIClient()).tools_get_answer_with_ai(null, data)
}

export async function loadAiRun (data) {
  return (await getMainAPIClient()).ai_runs_get(null, data)
}

// --- AI Writer v2 (redesign June 2026) -------------------------------------
// These endpoints are served by the new flask-smorest blueprint but called via
// mainAxios (not the generated OpenAPI client) so they work without an
// openapi.json regeneration. Auth + sub-account handling mirror the other
// mainAxios wrappers above.
function _aiWriterRequestConfig (token) {
  const subAccountRelationId = store.state?.selectedSubAccountRelation?.id
  const config = { headers: authHeader(token) }
  if (subAccountRelationId) {
    config.params = { subAccountRelationId }
  }
  return config
}

// Admin Prompt Inspector: any AI Writer response whose progress carries captured
// LLM prompts (admin-driven runs only) is fed into the store so the modal can
// show them. Inert otherwise; never breaks the response chain.
function _ingestPrompts (res) {
  try {
    const prompts = res?.data?.progress?.prompts
    if (prompts && prompts.length) store.commit('AIW_APPEND_PROMPTS', prompts)
  } catch (e) { /* ignore */ }
  return res
}

export function aiWriterCreate (token, data) {
  return mainAxios.post(`${API_URL}/ai-writer/create/`, data, _aiWriterRequestConfig(token))
}

export function aiWriterGetState (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/`, _aiWriterRequestConfig(token)).then(_ingestPrompts)
}

// Resume the most recent v2 run for a page run (ongoing or completed); resolves
// with `session: null` when there is none yet.
export function aiWriterGetLatest (token, pageRunId) {
  const config = _aiWriterRequestConfig(token)
  config.params = { ...(config.params || {}), pageRunId }
  return mainAxios.get(`${API_URL}/ai-writer/latest/`, config).then(_ingestPrompts)
}

export function aiWriterSaveSetup (token, aiRunId, setup) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/setup/`, { setup }, _aiWriterRequestConfig(token))
}

export function aiWriterScrapeUrl (token, aiRunId, url) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/scrape-url/`, { url }, _aiWriterRequestConfig(token))
}

export function aiWriterGetProgress (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/progress/`, _aiWriterRequestConfig(token)).then(_ingestPrompts)
}

// Stage 2 — planning pipeline + Title step
export function aiWriterPlan (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/plan/`, {}, _aiWriterRequestConfig(token))
}

export function aiWriterTitleGenerate (token, aiRunId, variantCount, charge, instruction) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/title/generate/`, { variantCount, charge: !!charge, instruction: instruction || '' }, _aiWriterRequestConfig(token))
}

export function aiWriterTitleRefine (token, aiRunId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/title/refine/`, data, _aiWriterRequestConfig(token))
}

export function aiWriterTitleSelect (token, aiRunId, data) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/title/`, data, _aiWriterRequestConfig(token))
}

export function aiWriterTitleLeaveAsIs (token, aiRunId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/title/leave-as-is/`, data, _aiWriterRequestConfig(token))
}

// Stage 3 — Draft (structure + content blocks + images)
export function aiWriterStructureGenerate (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/structure/generate/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterStructureRegenerate (token, aiRunId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/structure/regenerate/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterStructureSave (token, aiRunId, rows) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/structure/`, { rows }, _aiWriterRequestConfig(token))
}
export function aiWriterStructureLeaveAsIs (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/structure/leave-as-is/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterStructureExport (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/structure/export/`, _aiWriterRequestConfig(token))
}
export function aiWriterDraftGenerate (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/draft/generate/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterGetBlocks (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/blocks/`, _aiWriterRequestConfig(token))
}
export function aiWriterBlockPatch (token, aiRunId, blockId, data) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterBlockDelete (token, aiRunId, blockId) {
  return mainAxios.delete(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/`, _aiWriterRequestConfig(token))
}
export function aiWriterBlockRegenerate (token, aiRunId, blockId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/regenerate/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterBlockHistory (token, aiRunId, blockId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/history/`, _aiWriterRequestConfig(token))
}
export function aiWriterBlockRestore (token, aiRunId, blockId, versionId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/restore/`, { versionId }, _aiWriterRequestConfig(token))
}
export function aiWriterStructureHistory (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/structure/history/`, _aiWriterRequestConfig(token))
}
export function aiWriterStructureRestore (token, aiRunId, versionId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/structure/restore/`, { versionId }, _aiWriterRequestConfig(token))
}
export function aiWriterTitleHistory (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/title/history/`, _aiWriterRequestConfig(token))
}
export function aiWriterTitleRestore (token, aiRunId, versionId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/title/restore/`, { versionId }, _aiWriterRequestConfig(token))
}
export function aiWriterBlockImage (token, aiRunId, blockId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/blocks/${blockId}/image/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterContinueToSeo (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/continue-to-seo/`, {}, _aiWriterRequestConfig(token))
}

// Stage 4 — SEO & Quality
export function aiWriterAnalyze (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/analyze/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterRescore (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/rescore/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterFixPreview (token, aiRunId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/fix/preview/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterFix (token, aiRunId, data) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/fix/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterIgnoreIssue (token, aiRunId, issueId, ignored) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/issues/${issueId}/`, { ignored }, _aiWriterRequestConfig(token))
}
export function aiWriterDiff (token, aiRunId) {
  return mainAxios.get(`${API_URL}/ai-writer/${aiRunId}/diff/`, _aiWriterRequestConfig(token))
}
export function aiWriterContinueToPublish (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/continue-to-publish/`, {}, _aiWriterRequestConfig(token))
}

// Stage 5 — Publish + Fast Draft
export function aiWriterFastDraft (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/fast-draft/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterMetadataPatch (token, aiRunId, data) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/metadata/`, data, _aiWriterRequestConfig(token))
}
export function aiWriterMetadataReview (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/metadata-review/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterTermNotes (token, aiRunId, section, text) {
  return mainAxios.patch(`${API_URL}/ai-writer/${aiRunId}/term-notes/`, { section, text }, _aiWriterRequestConfig(token))
}
export function aiWriterNewVersion (token, aiRunId) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/new-version/`, {}, _aiWriterRequestConfig(token))
}
export function aiWriterStartOver (token, aiRunId, preserveSetup) {
  return mainAxios.post(`${API_URL}/ai-writer/${aiRunId}/start-over/`, { preserveSetup }, _aiWriterRequestConfig(token))
}
// ---------------------------------------------------------------------------

export async function aiRunChangeCurrentSelection(data) {
  return (await getMainAPIClient()).ai_runs_change_selection(null, data)
}

export async function aiRunSaveEditedContent(data) {
  return (await getMainAPIClient()).ai_runs_save_edited_content(null, data)
}

export async function aiRunRemoveContent(data) {
  return (await getMainAPIClient()).ai_runs_remove_content(null, data)
}

export async function aiRunChangeCurrentSelectionForRichItem (data) {
  return (await getMainAPIClient()).ai_runs_change_selection_rich_item(null, data)
}

export async function aiRunUpdateEditedContentForRichItem (data) {
  return (await getMainAPIClient()).ai_runs_save_edited_content_rich_item(null, data)
}

export async function aiRunAIGeneratedImageCreate(data) {
  return (await getMainAPIClient()).ai_runs_generated_image_create(null, data)
}

export async function aiRunAIGeneratedImageDelete(data) {
  return (await getMainAPIClient()).ai_runs_generated_image_delete(null, data)
}

export async function deleteAIRun (data) {
  return (await getMainAPIClient()).ai_runs_delete(null, data)
}

export async function aiRunGetFullHtml (data) {
  return (await getMainAPIClient()).ai_runs_get_full_html(null, data)
}

export async function createKeywordResearchCampaign (data) {
  return (await getMainAPIClient()).campaigns_create(null, data)
}

export async function getCampaignById (campaignId) {
  return (await getMainAPIClient()).campaigns_get({ campaign_id: campaignId })
}

export async function updateCampaign (campaignId, data) {
  return (await getMainAPIClient()).campaigns_update({ campaign_id: campaignId }, data)
}

export async function connectGoogleAccount (data) {
  return (await getMainAPIClient()).connectGoogleAccount(null, data)
}

export async function disconnectGoogleAccount () {
  return (await getMainAPIClient()).disconnectGoogleAccount()
}

export async function getGscConnection (siteId) {
  return (await getMainAPIClient()).getGscConnection({ site_id: siteId })
}

export async function connectGsc (siteId, data) {
  return (await getMainAPIClient()).connectGsc({ site_id: siteId }, data)
}

export async function updateGscSettings (siteId, data) {
  return (await getMainAPIClient()).updateGscSettings({ site_id: siteId }, data)
}

export async function disconnectGsc (siteId) {
  return (await getMainAPIClient()).disconnectGsc({ site_id: siteId })
}

export async function listGscProperties (siteId) {
  return (await getMainAPIClient()).listGscProperties({ site_id: siteId })
}

export async function getGoogleAccessToken () {
  return (await getMainAPIClient()).getGoogleAccessToken()
}

export async function listWinsTrackedUrls (siteId) {
  return (await getMainAPIClient()).listWinsTrackedUrls({ site_id: siteId })
}

export async function addWinsTrackedUrl (siteId, data) {
  return (await getMainAPIClient()).addWinsTrackedUrl({ site_id: siteId }, data)
}

export async function removeWinsTrackedUrl (trackedUrlId) {
  return (await getMainAPIClient()).removeWinsTrackedUrl({ tracked_url_id: trackedUrlId })
}

export async function saveDeliveredUrl (siloKeywordId, data) {
  return (await getMainAPIClient()).saveDeliveredUrl({ silo_keyword_id: siloKeywordId }, data)
}

export async function startWinsMappingRun (siteId) {
  return (await getMainAPIClient()).startWinsMappingRun({ site_id: siteId })
}

export async function getWinsMappingRun (siteId) {
  return (await getMainAPIClient()).getWinsMappingRun({ site_id: siteId })
}

export async function listWinsMappingSuggestions (siteId) {
  return (await getMainAPIClient()).listWinsMappingSuggestions({ site_id: siteId })
}

export async function approveWinsMappingSuggestion (suggestionId) {
  return (await getMainAPIClient()).approveWinsMappingSuggestion({ suggestion_id: suggestionId })
}

export async function declineWinsMappingSuggestion (suggestionId) {
  return (await getMainAPIClient()).declineWinsMappingSuggestion({ suggestion_id: suggestionId })
}

export async function undoDeclineWinsMappingSuggestion (suggestionId) {
  return (await getMainAPIClient()).undoDeclineWinsMappingSuggestion({ suggestion_id: suggestionId })
}

export async function getWinsReportSettings (siteId) {
  return (await getMainAPIClient()).getWinsReportSettings({ site_id: siteId })
}

export async function updateWinsReportSettings (siteId, data) {
  return (await getMainAPIClient()).updateWinsReportSettings({ site_id: siteId }, data)
}

export async function listWinsPeriods (siteId) {
  return (await getMainAPIClient()).listWinsPeriods({ site_id: siteId })
}

export async function lookupWinsUrl (siteId, url) {
  return (await getMainAPIClient()).lookupWinsUrl({ site_id: siteId, url })
}

export async function updateWinsTrackedUrl (trackedUrlId, data) {
  return (await getMainAPIClient()).updateWinsTrackedUrl({ tracked_url_id: trackedUrlId }, data)
}

export async function sendWinsReportNow (siteId, body = {}) {
  return (await getMainAPIClient()).sendWinsReportNow({ site_id: siteId }, body)
}

export async function checkWinsData (siteId) {
  return (await getMainAPIClient()).checkWinsData({ site_id: siteId })
}

export async function listWinsReportSends (siteId) {
  return (await getMainAPIClient()).listWinsReportSends({ site_id: siteId })
}

export async function getWinsReportPdf (siteId, period) {
  return (await getMainAPIClient()).getWinsReportPdf(
    { site_id: siteId, period }, null, { responseType: 'blob' })
}

export async function getWinsReport (siteId, period) {
  return (await getMainAPIClient()).getWinsReport({ site_id: siteId, period })
}

export async function getCampaignLatestGeneratedReports (campaignId) {
  return (await getMainAPIClient()).campaigns_latest_reports({ campaign_id: campaignId })
}

export async function getCampaignsForSite (siteId) {
  return (await getMainAPIClient()).campaigns_list_for_site({ site_id: siteId })
}

export async function createKeywordInsightResearch (data) {
  return (await getMainAPIClient()).keyword_research_create(null, data)
}

export async function getKeywordResearchHistoryForCampaign (data) {
  return (await getMainAPIClient()).keyword_research_history(null, data)
}

export async function loadKeywordResearchData (data) {
  return (await getMainAPIClient()).keyword_research_load_one(null, data)
}

export async function updateKeywordsVisibility (data) {
  return (await getMainAPIClient()).keyword_research_update_visibility(null, data)
}

export async function keywordResearchAnalyzeWithAI(data) {
  return (await getMainAPIClient()).keyword_research_analyze_ai(null, data)
}

export async function updateKeywordLabel(data) {
  return (await getMainAPIClient()).keyword_research_update_label(null, data)
}

export async function getKeywordsFromTopic (data) {
  return (await getMainAPIClient()).support_keyword_get_from_topic(null, data)
}

export async function clusteringUserKeywordsExcel (excelFile) {
  const client = await getMainAPIClient()
  let formdata = new FormData()
  formdata.append("keywordsFile", excelFile)
  return client.support_keyword_clustering(null, formdata)
}

export async function createSupportKeywordResearch (data) {
  return (await getMainAPIClient()).support_keyword_research(null, data)
}

export async function getSupportKeywordResearchHistoryForCampaign (data) {
  return (await getMainAPIClient()).support_keyword_history(null, data)
}

export async function loadSupportKeywordResearchData (data) {
  return (await getMainAPIClient()).support_keyword_load_one(null, data)
}

export async function supportKeywordResearch_analyzeClustersWithAI (data) {
  return (await getMainAPIClient()).support_keyword_analyze_ai(null, data)
}

export async function saveKeywordWizardResult (data) {
  return (await getMainAPIClient()).keyword_wizard_save(null, data)
}

export async function loadRecentKeywordWizardData (data) {
  return (await getMainAPIClient()).keyword_wizard_history(null, data)
}

export async function loadKeywordWizardMonthlySearchAndExtras(data) {
  return (await getMainAPIClient()).keyword_wizard_monthly_search(null, data)
}

export async function loadKeywordWizardLabels(data) {
  return (await getMainAPIClient()).keyword_wizard_labels(null, data)
}

export async function wizardUpdateKeywordLabel(data) {
  return (await getMainAPIClient()).keyword_wizard_update_label(null, data)
}

export async function siloResearchList (data) {
  return (await getMainAPIClient()).silo_research_list({ campaignId: data.campaignId })
}

export async function siloResearchSingle (data) {
  return (await getMainAPIClient()).silo_research_get({
    research_id: data.id,
    ...(data.includeTopics != null ? { includeTopics: data.includeTopics } : {}),
    ...(data.includeKeywords != null ? { includeKeywords: data.includeKeywords } : {}),
  })
}

export async function siloResearchSinglePatch(data) {
  return (await getMainAPIClient()).silo_research_update({ research_id: data.id }, data)
}

export async function siloResearchSingleDelete(data) {
  return (await getMainAPIClient()).silo_research_delete({ research_id: data.id })
}

export async function siloResearchOneClickSiloCreate (data) {
  return (await getMainAPIClient()).silo_research_one_click_create(null, data)
}

export async function siloResearchCustomSiloCreate (data) {
  return (await getMainAPIClient()).silo_research_custom_create(null, data)
}

export async function siloResearchCustomSiloStartMetricsTask (data) {
  return (await getMainAPIClient()).silo_research_custom_start_metrics(null, data)
}

export async function topLevel_getKeywordsFromTopic (data) {
  return (await getMainAPIClient()).top_level_keyword_from_topic(null, data)
}

export async function topLevel_createSupportKeywordResearch (data) {
  return (await getMainAPIClient()).top_level_keyword_research(null, data)
}

export async function topLevel_analyzeClustersWithAI (data) {
  return (await getMainAPIClient()).top_level_keyword_analyze_ai(null, data)
}

export async function topLevel_getSupportKeywordResearchHistoryForCampaign (data) {
  return (await getMainAPIClient()).top_level_keyword_history(null, data)
}

export async function topLevel_loadSupportKeywordResearchData (data) {
  return (await getMainAPIClient()).top_level_keyword_load_one(null, data)
}

export async function addKeywordToTrack(data) {
  return (await getMainAPIClient()).keyword_track_add(null, data)
}

export async function bulkTrackKeywordToTrack(data) {
  return (await getMainAPIClient()).keyword_track_bulk_update(null, data)
}

export async function removeKeywordFromTrack(data) {
  return (await getMainAPIClient()).keyword_track_remove(null, data)
}

export async function updateKeywordTrack(data) {
  return (await getMainAPIClient()).keyword_track_update(null, data)
}

export async function getKeywordTrackForCampaign(campaignId) {
  return (await getMainAPIClient()).keyword_track_get({ campaign_id: campaignId })
}

export async function startKeywordTrack(data) {
  return (await getMainAPIClient()).keyword_track_start(null, data)
}

export async function updateKeywordResearchReport(data) {
  return (await getMainAPIClient()).keyword_track_report_update(null, data)
}

export async function deleteKeywordResearchReport(reportId) {
  return (await getMainAPIClient()).keyword_track_report_delete({ research_id: reportId })
}

export async function getMonthlySearches(data) {
  return (await getMainAPIClient()).tools_get_monthly_search(null, data)
}

export async function createKeywordGapAnalysis (data) {
  return (await getMainAPIClient()).keyword_gap_analyze(null, data)
}
export async function loadKeywordGapHistory (data) {
  return (await getMainAPIClient()).keyword_gap_history(null, data)
}

export async function loadOneKeywordGapData (data) {
  return (await getMainAPIClient()).keyword_gap_load_one(null, data)
}

export async function getProjectsForWorkstation (params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).sites_workstation_projects(query)
}

export async function getPagesForWorkstation(data) {
  const params = Object.fromEntries(new URLSearchParams(data.params))
  return (await getMainAPIClient()).pages_get_workstation({ site_id: data.siteId, ...params })
}

export async function getPageRunForWorkStation (data) {
  return (await getMainAPIClient()).page_runs_get_workstation({ site_id: data.siteId, page_id: data.pageId })
}

export async function askForHelpWithKeywords(data) {
  return (await getMainAPIClient()).silos_ask_for_help(null, data)
}

export async function getSilos(data) {
  return (await getMainAPIClient()).silos_search(data || {})
}

export async function getSilo(siloId) {
  return (await getMainAPIClient()).silos_get({ silo_id: siloId })
}

export async function createSilo(data) {
  return (await getMainAPIClient()).silos_create(null, data)
}

export async function updateSilo(siloId, data) {
  return (await getMainAPIClient()).silos_update({ silo_id: siloId }, data)
}

export async function deleteSilo(siloId) {
  return (await getMainAPIClient()).silos_delete({ silo_id: siloId })
}

export async function bulkDeleteSilos(data) {
  return (await getMainAPIClient()).silos_bulk_delete(null, data)
}

export async function addKeywordToSilo(siloId, data) {
  return (await getMainAPIClient()).silos_add_keyword({ silo_id: siloId }, data)
}

export async function addKeywordsToSilo(siloId, data) {
  return (await getMainAPIClient()).silos_add_bulk_keywords({ silo_id: siloId }, data)
}

export async function removeKeywordFromSilo(siloKeywordId) {
  return (await getMainAPIClient()).silos_remove_keyword({ silo_keyword_id: siloKeywordId })
}

export async function restoreSiloKeyword(siloKeywordId) {
  return (await getMainAPIClient()).silos_restore_keyword({ silo_keyword_id: siloKeywordId })
}

export async function bulkRemoveKeywordFromSilo(data) {
  return (await getMainAPIClient()).silos_bulk_remove_keywords(null, data)
}

export async function bulkRestoreSiloKeyword(data) {
  return (await getMainAPIClient()).silos_bulk_restore_keywords(null, data)
}

export async function getSiloKeywords(params) {
  return (await getMainAPIClient()).silos_search_keywords(params || {})
}

export async function getSiloKeyword(siloKeywordId) {
  return (await getMainAPIClient()).silos_fetch_keyword({ silo_keyword_id: siloKeywordId })
}

export async function updateSiloKeyword(siloKeywordId, data) {
  return (await getMainAPIClient()).silos_update_keyword({ silo_keyword_id: siloKeywordId }, data)
}

export async function moveSiloKeywords(siloId, data) {
  return (await getMainAPIClient()).silos_move_keywords({ silo_id: siloId }, data)
}

export async function getSiloPDFReport(data, subAccountRelationId) {
  return (await getMainAPIClient()).silos_get_pdf_report(
    {
      silo_keyword_id: data.siloKeywordId,
      reportType: data.reportType,
      secretKey: data.secretKey,
      sourceType: data.sourceType,
      subAccountRelationId,
      ...(data.ommitInstructions ? { ommitInstructions: 1 } : {}),
    },
    null,
    { responseType: 'blob' },
  )
}

export async function getSiloWordReport(data, subAccountRelationId) {
  return (await getMainAPIClient()).silos_get_word_report({
    silo_keyword_id: data.siloKeywordId,
    reportType: data.reportType,
    secretKey: data.secretKey,
    sourceType: data.sourceType,
    subAccountRelationId,
  })
}

export async function getReportHeader(data, subAccountRelationId) {
  return (await getMainAPIClient()).reports_get_header_image({
    reportType: data.reportType,
    subAccountRelationId
  })
}

export async function getAvailableSiloContentReportPackages () {
  return (await getMainAPIClient()).silo_content_report_orders_available_packages()
}

export async function getAvailableSiloContentReportDeliveryOptionsForSite (data) {
  return (await getMainAPIClient()).silo_content_report_orders_available_delivery_options(data || {})
}

export async function getSiloContentReportOrders (data) {
  return (await getMainAPIClient()).silo_content_report_orders_list(data || {})
}

export async function updateSiloContentReportOrder (orderId, data) {
  return (await getMainAPIClient()).silo_content_report_orders_update({ order_id: orderId }, data)
}

export async function getCartItems (data) {
  return (await getMainAPIClient()).cart_get_items(data || {})
}

export async function getCartInvoices (data) {
  return (await getMainAPIClient()).cart_get_invoices(data || {})
}

export async function closeCartInvoice (cartInvoiceId) {
  return (await getMainAPIClient()).cart_close_invoice({ cart_invoice_id: cartInvoiceId })
}

export async function setCartItemsAsPaid () {
  return (await getMainAPIClient()).cart_set_items_as_paid(null, {})
}

export async function getTaskStatus(taskId) {
  return (await getMainAPIClient()).tasks_get_status({ task_id: taskId })
}

export async function getActiveTasks(data) {
  return (await getMainAPIClient()).tasks_list(data || {})
}

export async function acknowledgeTask(data) {
  return (await getMainAPIClient()).tasks_acknowledge(null, data)
}

export async function abortTask(data) {
  return (await getMainAPIClient()).tasks_abort(null, data)
}

export async function updateSiloKeywordFlag(data) {
  return (await getMainAPIClient()).silos_update_keyword_flag({ silo_keyword_id: data.siloKeywordId }, data)
}

export async function fetchUserHasPayments() {
  return (await getMainAPIClient()).users_has_payment_source()
}

export function resetBalanceForUser(token, data) {
  const headers = authHeader(token);
  return mainAxios.post(`${API_URL}/admin/reset-balance/`, data, { headers });
}

export function fetchPOPCharges(token) {
  const headers = authHeader(token);
  return mainAxios.get(`${API_URL}/pop-credit-charges/`, { headers });
}
export async function submitPopFeedback(payload) {
  return (await getMainAPIClient()).feedback_create(null, payload)
}
export async function getFeedbacksForAdmin(params) {
  const query = Object.fromEntries(new URLSearchParams(params))
  return (await getMainAPIClient()).feedback_admin_list(query)
}
export async function getFeedbackStatus() {
  return (await getMainAPIClient()).feedback_get_status()
}
export function getFeedbackDownload(token) {
  // Kept as raw axios — CSV download requires `responseType: 'blob'`,
  // which the openapi-client wrapper does not surface through generated operations.
  const headers = authHeader(token)
  return mainAxios.get(`${API_URL}/admin/feedback/export/`, {
    headers,
    responseType: 'blob'
  })
}
export function getReviewOptimizationOrders(token, data, subAccountRelationId) {
  const headers = authHeader(token);
  return mainAxios.get(
    `${API_URL}/review-optimization-orders/${data ? ('?' + (new URLSearchParams(data)).toString()) : ''}`,
    { headers, params: { subAccountRelationId } }
  );
}

export function createReviewOptimizationOrder(token, data, subAccountRelationId) {
  const headers = authHeader(token)
  return mainAxios.post(`${API_URL}/review-optimization-orders/`, data, { headers, params: { subAccountRelationId } })
}

export function updateReviewOptimizationOrder(token, orderId, data, subAccountRelationId) {
  const headers = authHeader(token)
  return mainAxios.patch(`${API_URL}/review-optimization-orders/${orderId}/`, data, { headers, params: { subAccountRelationId } })
}

export function getReviewOptimizationOrderPDFReport(token, data, subAccountRelationId) {
  const baseUrl = `${API_URL}/review-optimization-orders/${data.orderId}/pdf_report/`;
  const config = {
    headers: authHeader(token),
    responseType: "blob",
    params: {
      reportType: data.reportType,
      secretKey: data.secretKey,
      sourceType: data.sourceType,
      ...data.ommitInstructions?{ommitInstructions: 1}:{},
      subAccountRelationId
    }
  };
  
  return mainAxios.get(baseUrl, config);
}

export function getReviewOptimizationOrdersPricingInfo(token, subAccountRelationId) {
  const headers = authHeader(token);
  return mainAxios.get(
    `${API_URL}/review-optimization-orders/pricing-info/`,
    { headers, params: { subAccountRelationId } }
  );
}


export function getReviewOptimizationOrdersMetrics(token, subAccountRelationId) {
  const headers = authHeader(token);
  return mainAxios.get(
    `${API_URL}/review-optimization-orders/metrics/`,
    { headers, params: { subAccountRelationId } }
  );
}

// --- Signup / login redesign (specs/2026-10 - Signup redesign) -------------------------------
// Every outcome is HTTP 200 with { status, action }; the pages branch on `action`.

export async function signupStart (data) {
  return (await getMainAPIClient()).auth_signup(null, data)
}

export async function signupVerify (data) {
  return (await getMainAPIClient()).auth_signup_verify(null, data)
}

export async function signupResend (data) {
  return (await getMainAPIClient()).auth_signup_resend(null, data)
}

export async function saveWelcomeAnswers (data) {
  return (await getMainAPIClient()).users_welcome_answers(null, data)
}

export async function setPendingFirstRun (data) {
  return (await getMainAPIClient()).users_set_pending_first_run(null, data)
}

export async function clearPendingFirstRun () {
  return (await getMainAPIClient()).users_clear_pending_first_run()
}

export async function fetchPlanCatalog () {
  return (await getDefaultAPIClient()).plans_catalog()
}

export async function previewCoupon (data) {
  return (await getMainAPIClient()).plans_coupon_preview(null, data)
}

export async function createCheckoutSession (data) {
  return (await getMainAPIClient()).plans_create_checkout_session(null, data)
}

export async function getCheckoutStatus (sessionId) {
  return (await getMainAPIClient()).plans_checkout_status({ sessionId })
}

export async function fetchFairUsePolicy () {
  return (await getDefaultAPIClient()).fair_use_policy()
}

export async function loginWithPassword (data) {
  return (await getMainAPIClient()).auth_login(null, data)
}

export async function loginConfirmCode (data) {
  return (await getMainAPIClient()).auth_login_verify(null, data)
}

export async function loginResendCode (data) {
  return (await getMainAPIClient()).auth_login_resend(null, data)
}

export async function requestPasswordReset (data) {
  return (await getMainAPIClient()).auth_password_forgot(null, data)
}

export async function validateResetToken (token) {
  return (await getMainAPIClient()).auth_password_reset_validate({ token })
}

export async function resetPasswordWithToken (data) {
  return (await getMainAPIClient()).auth_password_reset(null, data)
}

export async function socialExchange (data) {
  return (await getMainAPIClient()).auth_social_exchange(null, data)
}

export async function socialConfirm (data) {
  return (await getMainAPIClient()).auth_social_confirm(null, data)
}

export async function socialLink (data) {
  return (await getMainAPIClient()).auth_social_link(null, data)
}
